/**
 * Missed Notification Recovery — client-side tap tracker.
 *
 * WHY THIS EXISTS
 * ---------------
 * Push notifications on iOS Safari PWA are single-shot: once the user taps
 * one, iOS clears it from the tray. If anything (e.g. the daily-verse
 * modal freeze bug we just fixed, or a network hiccup) prevented the app
 * from actually opening the target content, the notification is *gone* —
 * there's no OS-side way to get it back.
 *
 * This module gives us a lightweight client-side "recovery record": every
 * time the client observes a URL carrying the SW-appended ``?nt=1``
 * notification-tap marker, we snapshot ``{ts, path, search, type, refId}``
 * to localStorage with ``consumed=false``. The consuming component
 * (NewsDetailModal opening, BroadcastModal opening, or a dwell timer on
 * /events / /prayers) later calls ``markConsumed`` so we know the user
 * successfully saw the content. If the app is re-opened WITHOUT the
 * marker within 10 minutes AND the record is still unconsumed, the
 * :file:`MissedNotificationBanner` component fires a persistent Sonner
 * toast offering to re-navigate the user to the missed content.
 *
 * Notes
 * -----
 * * ``type === "generic"`` (bare ``/?nt=1``) is intentionally NOT tracked
 *   because that pattern is used by the daily-verse push and by the SW's
 *   fallback path — offering to "recover a missed daily verse" would be
 *   noise since the verse re-shows the next day automatically.
 * * The record is per-device, not per-account: we don't need to sync it
 *   across devices because the recovery window is only ~10 minutes.
 */
const KEY = "ncl_last_notification_tap";
export const RECOVERY_WINDOW_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Classify a URL into a notification type so the recovery banner can
 * render the right copy. Returns ``null`` for URLs we should NOT track
 * (the caller uses that to skip recording).
 */
export function classifyTap(pathname, search) {
  const params = new URLSearchParams(search || "");
  const newsId = params.get("news");
  const broadcastId = params.get("broadcast");
  if (newsId) return { type: "news", refId: newsId };
  if (broadcastId) return { type: "broadcast", refId: broadcastId };
  if (pathname === "/events" || pathname.startsWith("/events/")) {
    return { type: "event", refId: null };
  }
  if (pathname === "/prayers" || pathname.startsWith("/prayers/")) {
    return { type: "prayer", refId: null };
  }
  // Anything else (`/`, `/give`, `/gallery`, ...) is a "generic" tap that
  // we don't offer recovery for — no clear content to recover.
  return null;
}

function safeGet() {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
}

function safeSet(record) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(KEY, JSON.stringify(record));
  } catch (_e) {}
}

function safeClear() {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(KEY);
  } catch (_e) {}
}

/**
 * Record a notification tap. Called from PublicShell whenever the URL
 * transitions to include ``?nt=1``. Idempotent: if the caller re-invokes
 * with the exact same path+search combination we already recorded, we
 * keep the original timestamp so a client-side re-render doesn't reset
 * the 10-minute recovery window.
 */
export function recordTap(pathname, search) {
  const classification = classifyTap(pathname, search);
  if (!classification) return; // untracked (e.g. daily-verse-only tap)
  const prev = safeGet();
  const record = {
    ts: prev && prev.path === pathname && prev.search === search ? prev.ts : Date.now(),
    path: pathname,
    search: search || "",
    type: classification.type,
    refId: classification.refId,
    consumed: false,
  };
  safeSet(record);
}

/**
 * Mark the current recovery record as consumed. Called when the user
 * successfully reached / viewed the deep-linked content (news modal
 * opened, broadcast modal opened, or after a dwell timer on the
 * destination page). Also used by the recovery banner itself to close
 * the loop after a user taps "View" or dismisses the toast.
 *
 * ``expectedType`` is optional — if provided, we only clear the record
 * when it matches, so a Homepage "news consumed" event can't accidentally
 * discard a pending broadcast recovery banner.
 */
export function markConsumed(expectedType) {
  const rec = safeGet();
  if (!rec) return;
  if (expectedType && rec.type !== expectedType) return;
  safeClear();
}

/**
 * Return the pending recovery record if one exists AND is still eligible
 * to show a recovery banner:
 *   * unconsumed
 *   * inside the 10-minute window
 *   * points to a different route than the user is currently on (no
 *     point offering to "recover" a page they're already looking at)
 *
 * Returns ``null`` otherwise. Callers can safely treat null as "no
 * banner needed".
 */
export function getPendingRecovery(currentPath) {
  const rec = safeGet();
  if (!rec || rec.consumed) return null;
  if (typeof rec.ts !== "number") return null;
  if (Date.now() - rec.ts > RECOVERY_WINDOW_MS) {
    // Stale; garbage-collect so a later tap in the same tab starts fresh.
    safeClear();
    return null;
  }
  if (currentPath && rec.path === currentPath) return null;
  return rec;
}
